import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeName, ThemeConfig, ThemeContextType, THEME_CONFIGS, ThemeVocabulary } from '../types/theme.types';

const NintendoQuestThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface NintendoQuestThemeProviderProps {
  children: ReactNode;
}

export const NintendoQuestThemeProvider: React.FC<NintendoQuestThemeProviderProps> = ({ children }) => {
  // Initialize with default theme, but check localStorage for persistence
  const [currentTheme, setCurrentTheme] = useState<ThemeName>(() => {
    const savedTheme = localStorage.getItem('nintendo-quest-theme');
    if (savedTheme && savedTheme in THEME_CONFIGS) {
      return savedTheme as ThemeName;
    }
    return 'default';
  });

  // Update localStorage when theme changes
  useEffect(() => {
    localStorage.setItem('nintendo-quest-theme', currentTheme);
  }, [currentTheme]);

  const themeConfig = THEME_CONFIGS[currentTheme];

  const setTheme = (theme: ThemeName) => {
    setCurrentTheme(theme);
  };

  // FIXED: Handle optional vocabulary fields properly
  const getVocabulary = (key: keyof ThemeVocabulary): string => {
    const value = themeConfig.vocabulary[key];
    // Return the value if it exists, otherwise return a fallback
    if (value !== undefined) {
      return value;
    }
    
    // Fallback values for optional fields
    const fallbacks: Partial<ThemeVocabulary> = {
      weeklyLog: '📅 Weekly Activities',
      perfectDays: 'perfect days',
      activeDays: 'active days',
      levels: '📊 Activity Levels:',
      noActivity: 'No Activity'
    };
    
    return fallbacks[key] || `${key}`;
  };

  const getColor = (path: string): string => {
    // Navigate through theme colors object using dot notation
    // e.g., 'primary' -> themeConfig.colors.primary
    const pathParts = path.split('.');
    let current: any = themeConfig.colors;
    
    for (const part of pathParts) {
      if (current[part] !== undefined) {
        current = current[part];
      } else {
        console.warn(`Theme color path '${path}' not found`);
        return 'gray-500'; // fallback
      }
    }
    
    return current;
  };

  const isChildTheme = (): boolean => {
    return themeConfig.targetAudience === 'child';
  };

  const isAdhdOptimized = (): boolean => {
    return themeConfig.adhdOptimized;
  };

  const contextValue: ThemeContextType = {
    currentTheme,
    themeConfig,
    setTheme,
    getVocabulary,
    getColor,
    isChildTheme,
    isAdhdOptimized
  };

  return (
    <NintendoQuestThemeContext.Provider value={contextValue}>
      {/* Only apply theme classes to this specific container, not the whole app */}
      <div className={`nintendo-quest-theme theme-${currentTheme}`}>
        {children}
      </div>
    </NintendoQuestThemeContext.Provider>
  );
};

// Custom hook for using Nintendo Quest theme context
export const useNintendoQuestTheme = (): ThemeContextType => {
  const context = useContext(NintendoQuestThemeContext);
  if (context === undefined) {
    throw new Error('useNintendoQuestTheme must be used within a NintendoQuestThemeProvider');
  }
  return context;
};

// Utility hook for theme-aware CSS classes - scoped to Nintendo Quest
export const useNintendoQuestThemeClasses = () => {
  const { themeConfig } = useNintendoQuestTheme();
  
  return {
    // Common patterns for easy use
    cardClass: `bg-${themeConfig.colors.cardBackground} border border-${themeConfig.colors.border} rounded-xl shadow-lg`,
    primaryButtonClass: `bg-${themeConfig.colors.primary} hover:bg-${themeConfig.colors.primary}/90 text-white font-semibold py-2 px-4 rounded-lg transition-colors`,
    accentTextClass: `text-${themeConfig.colors.accent}`,
    gradientBackgroundClass: `bg-${themeConfig.colors.background}`,
    
    // Helper function for custom combinations
    getThemeClass: (element: 'card' | 'button' | 'text' | 'background', variant?: string) => {
      const colors = themeConfig.colors;
      
      switch (element) {
        case 'card':
          return `bg-${colors.cardBackground} border border-${colors.border} rounded-xl shadow-lg`;
        case 'button':
          const color = variant === 'secondary' ? colors.secondary : colors.primary;
          return `bg-${color} hover:bg-${color}/90 text-white font-semibold py-2 px-4 rounded-lg transition-colors`;
        case 'text':
          const textColor = variant === 'accent' ? colors.accent : variant === 'secondary' ? colors.secondary : colors.text;
          return `text-${textColor}`;
        case 'background':
          return `bg-${colors.background}`;
        default:
          return '';
      }
    }
  };
};